// ─── Central App Config ────────────────────────────────────────────────────────
// All env/feature flags read here. Import this everywhere instead of
// reading import.meta.env directly in components.

const env = import.meta.env;

// Default main AlgoSys portal hostnames. index.html repeats this list for the
// pre-load tab title; indexHtmlPortalTitle.test.js keeps the two in sync.
export const DEFAULT_MAIN_PORTAL_HOSTNAMES = Object.freeze([
  'v2.algosys.co.in',
  'dev.algosys.co.in',
  'test.algosys.co.in',
  'localhost',
  '127.0.0.1',
  '[::1]',
]);

export const APP_CONFIG = {
  // API
  // Default to same-origin so production works behind Nginx and local Vite proxying
  // continues to work without a hardcoded backend host.
  apiBaseUrl: env.VITE_API_BASE_URL || '/',

  // App mode: 'standalone' | 'embedded'
  // In embedded mode the app is rendered inside Algosys iframe.
  appMode: env.VITE_APP_MODE || 'standalone',

  // Session mode: 'dev-open' | 'standalone-token' | 'algosys-embedded'
  // dev-open:          No auth required. Use devUser below.
  // standalone-token:  Token from localStorage (current behavior).
  // algosys-embedded:  Session bootstrapped from parent Algosys frame.
  // Missing build settings must never create a passwordless Admin session.
  sessionMode: env.VITE_SESSION_MODE || 'standalone-token',

  // Dev user (only used when sessionMode === 'dev-open')
  devUser: {
    id:       Number(env.VITE_DEV_USER_ID) || 1,
    username: env.VITE_DEV_USERNAME  || 'dev',
    name:     env.VITE_DEV_NAME      || 'Dev User',
    role:     env.VITE_DEV_ROLE      || 'admin', // 'user' | 'admin' | 'both'
  },

  // Chrome extension secure connect flow (Phase 2F).
  // Both are non-secret, exact-match identifiers the extension's
  // authorization page must compare byte-for-byte against the incoming
  // request before ever calling /extension/auth/authorize or redirecting
  // back to the extension. Empty by default (feature inert/fails closed
  // until an operator configures both to match the backend's
  // EXTENSION_AUTH_ALLOWED_CLIENT_ID / EXTENSION_AUTH_ALLOWED_REDIRECT_URI
  // and the extension's own hardcoded client id / chrome.identity
  // getRedirectURL() value).
  extension: {
    clientId:    env.VITE_EXTENSION_CLIENT_ID    || '',
    redirectUri: env.VITE_EXTENSION_REDIRECT_URI || '',
  },

  // Hostnames served as the main AlgoSys portal rather than an Operator
  // white-label portal.
  //
  // PRESENTATION ONLY. This decides whether AlgoSys's own marketing copy,
  // WhatsApp enquiry link and company attribution may be shown. It must never
  // influence authentication, Operator assignment, or backend tenant
  // authorization — the backend's trusted ingress contract remains the sole
  // authority there, and an unrecognised host simply falls back to neutral
  // presentation rather than gaining or losing any access.
  //
  // Defaults cover the AlgoSys-owned application hosts (production, DEV, test)
  // plus local development. Override with a comma-separated list if another
  // AlgoSys-owned host is added.
  mainPortalHostnames: (
    env.VITE_MAIN_PORTAL_HOSTNAMES
    || DEFAULT_MAIN_PORTAL_HOSTNAMES.join(',')
  )
    .split(',')
    .map((host) => host.trim().toLowerCase())
    .filter(Boolean),

  // Feature flags
  features: {
    adminEnabled:     env.VITE_ADMIN_ENABLED     !== 'false',
    embeddedReady:    env.VITE_EMBEDDED_READY     !== 'false',
    realtimePolling:  env.VITE_REALTIME_POLLING   !== 'false',
    aiBuilder:        false,  // Enable AI-assisted strategy builder (future)
  },

  // Poll intervals (ms)
  poll: {
    health:     5_000,
    deployment: 5_000,
    signals:    5_000,
    dispatch:   8_000,
    logs:       5_000,
    strategies: 30_000,
  },
};

export const isEmbedded   = APP_CONFIG.appMode     === 'embedded';
export const isDevOpen    = APP_CONFIG.sessionMode === 'dev-open';
export const isAdminEnabled = APP_CONFIG.features.adminEnabled;

// There is deliberately no static Razorpay key here. The merchant account for
// every purchase is decided server-side and returned as `key_id` by
// POST /billing/orders, so the browser cannot route a payment to the wrong
// merchant and cannot fall back to a build-time key.

export default APP_CONFIG;
